/**
 * tenant_site_settings stores logo_url / favicon_url as relative paths:
 *   /tenant-assets/{tenantId}/logo
 *
 * items.image_url and Site Builder gallery images use media serve paths:
 *   /api/v1/media/{id}/serve?t={slug}
 *   (sometimes stored absolute, e.g. https://api.busmgr.com/api/v1/media/…)
 *
 * Dev: CRA proxy forwards /tenant-assets and /api/v1 to the backend.
 * Prod: the storefront SPA has no such proxy — relative paths hit Vercel and
 * return HTML 404. Rewrite to the absolute API origin in production.
 */

import { API_URL } from '../services/apiConfig';
import { getTenantFromSubdomain } from '../services/tenant';
import { resolveMediaServeUrl } from '../../../packages/blocks/src/utils/mediaServeUrls';
import { withUploadsTenantQuery } from './uploadsTenantUrls';

export function resolveTenantAssetUrl(url) {
  if (!url || typeof url !== 'string') return url;

  // Media library paths — rebuild from media id (handles relative + absolute hosts).
  if (/(?:\/api\/v1)?\/media\/[0-9a-f-]{36}\/serve/i.test(url)) {
    return resolveMediaServeUrl(url, getTenantFromSubdomain(), API_URL) || url;
  }

  if (/^https?:\/\//i.test(url)) return url;

  const isBackendRelativePath = url.startsWith('/tenant-assets/');
  if (!isBackendRelativePath) return url;

  const apiOrigin = String(API_URL).replace(/\/api\/v1\/?$/, '');
  if (!apiOrigin) return url;

  return `${apiOrigin}${url}`;
}

/** Alias — item product photos use the same prod rewrite as logo/favicon. */
export const resolveItemImageUrl = resolveTenantAssetUrl;

/**
 * Blog featured images may still store local-dev upload URLs
 * (http://localhost:3000/uploads/...) from early imports. Rewrite those
 * (and relative /uploads|/api paths) to the API origin for storefront display.
 *
 * Legacy /uploads/{tenantId}/... is tenant-gated on the API — always attach
 * ?t=<current slug> so same-tenant files still load. Cross-tenant paths will
 * 404 at the API (correct); do not strip or rewrite them to another host.
 */
export function resolveBlogImageUrl(url) {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('data:')) return url;

  const tenantSlug = getTenantFromSubdomain();

  if (/(?:\/api\/v1)?\/media\/[0-9a-f-]{36}\/serve/i.test(url)) {
    return resolveMediaServeUrl(url, tenantSlug, API_URL) || url;
  }

  const apiOrigin = String(API_URL).replace(/\/api\/v1\/?$/, '');

  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url);
      if (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') {
        const pathWithTenant = withUploadsTenantQuery(
          `${parsed.pathname}${parsed.search}`,
          tenantSlug
        );
        return `${apiOrigin}${pathWithTenant}`;
      }
      // Absolute API upload URLs still need ?t= for the tenant gate
      if (/\/uploads\//i.test(parsed.pathname)) {
        return withUploadsTenantQuery(url, tenantSlug);
      }
    } catch (_) {
      return url;
    }
    return url;
  }

  if (url.startsWith('/api/') || url.startsWith('/uploads')) {
    const withTenant = withUploadsTenantQuery(url, tenantSlug);
    return apiOrigin ? `${apiOrigin}${withTenant}` : withTenant;
  }

  return url;
}

export default resolveTenantAssetUrl;
